import React, { useState, useCallback, useEffect, useRef } from "react";
import SearchIcon from "@mui/icons-material/Search";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Typography,
  Box,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  Chip,
  Avatar,
  Paper,
  Skeleton,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import BusinessIcon from "@mui/icons-material/Business";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import IconButton from "@mui/material/IconButton";
import { Autocomplete, TextField, Tooltip } from "@mui/material";
import { injectIntl } from "react-intl";
import Api from "../../../../services/api";
import * as CalledService from "../../../../services/called.service";

const PAGE_SIZE = 20;

function MassMoveCalledModal({ open, onClose, onShowNotification, lang, intl }) {
  const [activeStep, setActiveStep] = useState(0);

  // Step 1
  const [technicianOptions, setTechnicianOptions] = useState([]);
  const [technicianLoading, setTechnicianLoading] = useState(false);
  const [sourceTechnician, setSourceTechnician] = useState(null);
  const [destTechnician, setDestTechnician] = useState(null);

  // Step 2
  const [calleds, setCalleds] = useState([]);
  const [calledsLoading, setCalledsLoading] = useState(false);
  const [calledsPage, setCalledsPage] = useState(1);
  const [calledsHasMore, setCalledsHasMore] = useState(false);
  const [calledsLoadingMore, setCalledsLoadingMore] = useState(false);
  const [selectedCalledIds, setSelectedCalledIds] = useState([]);
  const [searchId, setSearchId] = useState("");
  const [searchedCalleds, setSearchedCalleds] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchDebounceRef = useRef(null);

  const [reason, setReason] = useState("");

  // Step 3
  const [moving, setMoving] = useState(false);
  const [editingDestTechnician, setEditingDestTechnician] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const steps = [
    intl.formatMessage({ id: "mass.move.step.select.technician" }),
    intl.formatMessage({ id: "mass.move.step.select.calleds" }),
    intl.formatMessage({ id: "mass.move.step.confirm" }),
  ];

  const handleClose = useCallback(() => {
    setActiveStep(0);
    setSourceTechnician(null);
    setDestTechnician(null);
    setCalleds([]);
    setSelectedCalledIds([]);
    setCalledsPage(1);
    setCalledsHasMore(false);
    setTechnicianOptions([]);
    setReason("");
    setEditingDestTechnician(false);
    setSearchId("");
    setSearchQuery("");
    setSearchedCalleds([]);
    onClose();
  }, [onClose]);

  const loadTechnicians = useCallback(async () => {
    setTechnicianLoading(true);
    try {
      const result = await CalledService.getTechinicalUsersDapper();
      if (result && result.success) {
        const sorted =
          result.data && result.data.length > 1
            ? [...result.data].sort((a, b) => a.name.localeCompare(b.name))
            : result.data || [];
        setTechnicianOptions(
          sorted.map((u) => ({
            id: u.id,
            label: u.name || u.email || "",
            email: u.email || "",
          }))
        );
      }
    } catch (err) {
      onShowNotification &&
        onShowNotification("error", intl.formatMessage({ id: "process.error" }));
    } finally {
      setTechnicianLoading(false);
    }
  }, [intl, onShowNotification]);

  const handleTechnicianOpen = useCallback(() => {
    if (technicianOptions.length === 0 && !technicianLoading) {
      loadTechnicians();
    }
  }, [technicianOptions.length, technicianLoading, loadTechnicians]);

  const loadCalleds = useCallback(
    async (userId, page, append = false) => {
      if (append) setCalledsLoadingMore(true);
      else setCalledsLoading(true);
      try {
        const url =
          `/calleds/paginate?page=${page}&type=0` +
          `&search=2|${userId}|&field=statusid|assigneduserid|` +
          `&osearch=2|&ofield=open|` +
          `&isfinished=0&isarchived=0&iscanceled=0`;
        const result = await Api.get(url);
        if (result && result.data && result.data.success) {
          const data = result.data.data || [];
          const total = result.data.count || 0;
          if (append) {
            setCalleds((prev) => [...prev, ...data]);
          } else {
            setCalleds(data);
          }
          setCalledsHasMore(page * PAGE_SIZE < total);
        }
      } catch (err) {
        onShowNotification &&
          onShowNotification("error", intl.formatMessage({ id: "process.error" }));
      } finally {
        setCalledsLoading(false);
        setCalledsLoadingMore(false);
      }
    },
    [intl, onShowNotification]
  );

  const handleNext = useCallback(async () => {
    if (activeStep === 0) {
      if (!sourceTechnician || !destTechnician || !reason.trim()) return;
      setCalledsPage(1);
      setSelectedCalledIds([]);
      setSearchId("");
      setSearchQuery("");
      setSearchedCalleds([]);
      setActiveStep(1);
      loadCalleds(sourceTechnician.id, 1, false);
    } else if (activeStep === 1) {
      if (selectedCalledIds.length === 0) return;
      setActiveStep(2);
    }
  }, [activeStep, sourceTechnician, destTechnician, reason, selectedCalledIds, loadCalleds]);

  const handleBack = useCallback(() => {
    setActiveStep((prev) => prev - 1);
  }, []);

  const handleLoadMore = useCallback(async () => {
    const nextPage = calledsPage + 1;
    setCalledsPage(nextPage);
    await loadCalleds(sourceTechnician.id, nextPage, true);
  }, [calledsPage, sourceTechnician, loadCalleds]);

  const handleToggleCalled = useCallback((id) => {
    setSelectedCalledIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const [searchQuery, setSearchQuery] = useState(""); // ID efetivamente pesquisado (após clique)

  const executeSearch = useCallback(async () => {
    if (!searchId || !sourceTechnician) return;
    setSearchQuery(searchId);
    setSearchLoading(true);
    try {
      const url =
        `/calleds/paginate?page=1&type=0` +
        `&search=2|${sourceTechnician.id}|${searchId}&field=statusid|assigneduserid|id` +
        `&osearch=2|&ofield=open|` +
        `&isfinished=0&isarchived=0&iscanceled=0`;
      const result = await Api.get(url);
      if (result?.data?.success) {
        const data = result.data.data || [];
        const found = data.find((c) => c.id === parseInt(searchId, 10));
        setSearchedCalleds((prev) =>
          found && !prev.find((c) => c.id === found.id) ? [...prev, found] : prev
        );
      }
    } catch (_) {
      // silencioso
    } finally {
      setSearchLoading(false);
    }
  }, [searchId, sourceTechnician]);

  // Lista exibida: searchQuery ativo → só o resultado da busca; limpo → selecionados via busca fixos no topo + lista normal
  const displayCalleds = searchQuery
    ? searchedCalleds.filter((c) => c.id === parseInt(searchQuery, 10))
    : [
        ...searchedCalleds.filter((s) => selectedCalledIds.includes(s.id) && !calleds.find((c) => c.id === s.id)),
        ...calleds,
      ];

  const handleToggleAll = useCallback(() => {
    setSelectedCalledIds((prev) =>
      prev.length === displayCalleds.length ? [] : displayCalleds.map((c) => c.id)
    );
  }, [displayCalleds]);

  const handleConfirmMove = useCallback(() => {
    if (!destTechnician || selectedCalledIds.length === 0) return;
    setConfirmOpen(true);
  }, [destTechnician, selectedCalledIds]);

  const handleExecuteMove = useCallback(async () => {
    setConfirmOpen(false);
    setMoving(true);
    try {
      await Api.post("/called-redistribution", {
        sourceUserId: sourceTechnician.id,
        targetUserId: destTechnician.id,
        calledIds: selectedCalledIds,
        reason: reason.trim(),
      });
      onShowNotification &&
        onShowNotification("success", intl.formatMessage({ id: "mass.move.success" }));
      handleClose();
    } catch (err) {
      onShowNotification &&
        onShowNotification("error", intl.formatMessage({ id: "process.error" }));
      setMoving(false);
    }
  }, [sourceTechnician, destTechnician, selectedCalledIds, reason, intl, onShowNotification, handleClose]);

  const allKnownCalleds = [
    ...searchedCalleds.filter((s) => !calleds.find((c) => c.id === s.id)),
    ...calleds,
  ];
  const selectedCalleds = allKnownCalleds.filter((c) => selectedCalledIds.includes(c.id));

  const destOptions = technicianOptions.filter(
    (t) => !sourceTechnician || t.id !== sourceTechnician.id
  );
  const sourceOptions = technicianOptions.filter(
    (t) => !destTechnician || t.id !== destTechnician.id
  );

  const getCalledUrl = (id) =>
    lang ? `/${lang}/called-accompaniment/${id}` : `/called-accompaniment/${id}`;

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        handleClose();
      }}
      maxWidth="md"
      fullWidth
      scroll="body"
      aria-labelledby="mass-move-called-title"
    >
      <DialogTitle id="mass-move-called-title" sx={{ pb: 1, pr: 6 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <SwapHorizIcon color="primary" />
          <Typography variant="h6" component="span">
            {intl.formatMessage({ id: "mass.move.title" })}
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={moving}
          sx={{ position: "absolute", right: 8, top: 8, color: "text.secondary" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2, minHeight: activeStep === 0 ? "auto" : 480 }}>
        {moving ? (
          <Box sx={{ p: 1 }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 2 }}>
              <Skeleton variant="rounded" height={80} />
              <Skeleton variant="rounded" height={80} />
            </Box>
            <Skeleton variant="rounded" height={60} sx={{ mb: 2 }} />
            <Skeleton variant="rounded" width={160} height={20} sx={{ mb: 1 }} />
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 1.5 }}>
              {[...Array(6)].map((_, i) => <Skeleton key={i} variant="rounded" height={70} />)}
            </Box>
          </Box>
        ) : (
        <>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step 1: Select source + destination technician */}
        {activeStep === 0 && (
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600} color="text.secondary">
                {intl.formatMessage({ id: "mass.move.origin" })}
              </Typography>
              <Autocomplete
                options={sourceOptions}
                loading={technicianLoading}
                value={sourceTechnician}
                onOpen={handleTechnicianOpen}
                onChange={(_, v) => setSourceTechnician(v)}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                getOptionLabel={(opt) => opt.label || ""}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={intl.formatMessage({ id: "assigned" })}
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {technicianLoading ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.id}>
                    <Avatar sx={{ width: 28, height: 28, mr: 1, fontSize: 13, bgcolor: "primary.main" }}>
                      {(option.label || "?")[0].toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="body2">{option.label}</Typography>
                      {option.email && (
                        <Typography variant="caption" color="text.secondary">
                          {option.email}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600} color="text.secondary">
                {intl.formatMessage({ id: "mass.move.destination" })}
              </Typography>
              <Autocomplete
                options={destOptions}
                loading={technicianLoading}
                value={destTechnician}
                onOpen={handleTechnicianOpen}
                onChange={(_, v) => setDestTechnician(v)}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                getOptionLabel={(opt) => opt.label || ""}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={intl.formatMessage({ id: "assigned" })}
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {technicianLoading ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.id}>
                    <Avatar sx={{ width: 28, height: 28, mr: 1, fontSize: 13, bgcolor: "success.main" }}>
                      {(option.label || "?")[0].toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="body2">{option.label}</Typography>
                      {option.email && (
                        <Typography variant="caption" color="text.secondary">
                          {option.email}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sx={{ mt: 1 }}>
              <TextField
                label={intl.formatMessage({ id: "mass.move.reason" })}
                value={reason}
                onChange={(e) => setReason(e.target.value.slice(0, 255))}
                inputProps={{ maxLength: 255 }}
                helperText={`${reason.length}/255`}
                multiline
                rows={2}
                fullWidth
                required
                variant="outlined"
              />
            </Grid>
          </Grid>
        )}

        {/* Step 2: Select calleds to move */}
        {activeStep === 1 && (
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle1" fontWeight={500}>
                {intl.formatMessage({ id: "mass.move.select.calleds" })}
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                {selectedCalledIds.length > 0 && (
                  <Chip
                    size="small"
                    label={intl.formatMessage(
                      { id: "mass.move.selected.count" },
                      { count: selectedCalledIds.length }
                    )}
                    color="primary"
                  />
                )}
                <Button
                  size="small"
                  onClick={handleToggleAll}
                  disabled={displayCalleds.length === 0}
                  variant={selectedCalledIds.length === displayCalleds.length && displayCalleds.length > 0 ? "contained" : "outlined"}
                  color="primary"
                  startIcon={<DoneAllIcon sx={{ fontSize: 16 }} />}
                  disableElevation
                  sx={{ borderRadius: 2, textTransform: "none", fontWeight: 500 }}
                >
                  {intl.formatMessage({ id: "select.all" })}
                </Button>
              </Box>
            </Box>
            <Box display="flex" gap={0.75} mb={1.5}>
              <TextField
                size="small"
                placeholder={intl.formatMessage({ id: "called" }) + " ID"}
                value={searchId}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, "");
                  setSearchId(v);
                  if (!v) setSearchQuery("");
                }}
                onKeyDown={(e) => { if (e.key === "Enter") executeSearch(); }}
                sx={{ width: 170 }}
              />
              <IconButton
                size="small"
                onClick={executeSearch}
                disabled={!searchId || searchLoading}
                color="primary"
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, px: 1 }}
              >
                {searchLoading ? <CircularProgress size={16} /> : <SearchIcon fontSize="small" />}
              </IconButton>
            </Box>

            {calledsLoading || searchLoading ? (
              <Box>
                {[...Array(5)].map((_, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <Divider />}
                    <Box sx={{ display: "flex", alignItems: "flex-start", px: 1, py: 1.5, gap: 1 }}>
                      <Skeleton variant="rectangular" width={18} height={18} sx={{ mt: 0.5, borderRadius: 0.5, flexShrink: 0 }} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="55%" height={20} />
                        <Skeleton variant="text" width="35%" height={16} sx={{ mt: 0.5 }} />
                        <Box display="flex" gap={0.5} mt={0.5}>
                          <Skeleton variant="rounded" width={60} height={18} />
                          <Skeleton variant="rounded" width={50} height={18} />
                        </Box>
                      </Box>
                    </Box>
                  </React.Fragment>
                ))}
              </Box>
            ) : displayCalleds.length === 0 ? (
              <Box sx={{ mt: 1, p: 2, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {searchQuery && !searchLoading
                    ? intl.formatMessage({ id: "mass.move.search.not.found" }, { id: searchQuery })
                    : intl.formatMessage({ id: "mass.move.no.calleds" })}
                </Typography>
              </Box>
            ) : (
              <Paper variant="outlined" sx={{ maxHeight: 440, overflow: "auto" }}>
                <List dense disablePadding>
                  {displayCalleds.map((called, index) => (
                    <React.Fragment key={called.id}>
                      {index > 0 && <Divider component="li" />}
                      <ListItem disablePadding>
                        <ListItemButton onClick={() => handleToggleCalled(called.id)} sx={{ py: 1 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Checkbox
                              edge="start"
                              checked={selectedCalledIds.includes(called.id)}
                              tabIndex={-1}
                              disableRipple
                              size="small"
                            />
                          </ListItemIcon>
                          <ListItemText
                            disableTypography
                            primary={
                              <Typography
                                component="a"
                                href={getCalledUrl(called.id)}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="body2"
                                color="primary"
                                noWrap
                                sx={{ textDecoration: "none", display: "block", "&:hover": { textDecoration: "underline" } }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <strong>#{called.id}</strong> — {called.title}
                              </Typography>
                            }
                            secondary={
                              <Box sx={{ mt: 0.5 }}>
                                <Box display="flex" gap={1} mt={0.25}>
                                  {/* Esquerda: solicitante + organização + categoria */}
                                  <Box display="flex" flexDirection="column" gap={0.4} flex={1} minWidth={0}>
                                    {called.requestinguser?.name && (
                                      <Box display="flex" alignItems="center" gap={0.3}>
                                        <PersonIcon sx={{ fontSize: 14, color: "text.disabled", flexShrink: 0 }} />
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }} noWrap>
                                          {called.requestinguser.name}
                                        </Typography>
                                      </Box>
                                    )}
                                    {called.organization?.name && (
                                      <Box display="flex" alignItems="center" gap={0.3}>
                                        <BusinessIcon sx={{ fontSize: 14, color: "text.disabled", flexShrink: 0 }} />
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }} noWrap>
                                          {called.organization.name}
                                        </Typography>
                                      </Box>
                                    )}
                                    {called.organizationcategory?.name && (
                                      <Tooltip title={called.organizationcategory.parentname || called.organizationcategory.name} placement="bottom-start">
                                        <Box display="flex" alignItems="center" gap={0.3} minWidth={0}>
                                          <AccountTreeIcon sx={{ fontSize: 14, color: "text.disabled", flexShrink: 0 }} />
                                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }} noWrap>
                                            {called.organizationcategory.parentname || called.organizationcategory.name}
                                          </Typography>
                                        </Box>
                                      </Tooltip>
                                    )}
                                  </Box>
                                  {/* Direita: chips tamanho fixo + data */}
                                  <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.4} flexShrink={0} sx={{ width: 150 }}>
                                    {called.status?.name && (
                                      <Chip size="small" label={called.status.name} sx={{ width: "100%", height: 18, fontSize: 11, bgcolor: called.status.colorhex ? `${called.status.colorhex}22` : undefined, color: called.status.colorhex || "text.secondary", border: `1px solid ${called.status.colorhex || "transparent"}`, "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" } }} />
                                    )}
                                    {called.type?.name && (
                                      <Chip size="small" label={called.type.name} variant="outlined" sx={{ width: "100%", height: 18, fontSize: 11, "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" } }} />
                                    )}
                                    {called.priority?.name && (
                                      <Chip size="small" label={called.priority.name} sx={{ width: "100%", height: 18, fontSize: 11, bgcolor: called.priority.colorhex ? `${called.priority.colorhex}22` : undefined, color: called.priority.colorhex || "text.secondary", border: `1px solid ${called.priority.colorhex || "transparent"}`, "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" } }} />
                                    )}
                                    {called.dueDt && (
                                      <Box display="flex" alignItems="center" gap={0.3}>
                                        <ScheduleIcon sx={{ fontSize: 12, color: "text.disabled" }} />
                                        <Typography variant="caption" color="text.secondary">{called.dueDt}</Typography>
                                      </Box>
                                    )}
                                  </Box>
                                </Box>
                              </Box>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>

                {calledsHasMore && !searchQuery && (
                  <Box display="flex" justifyContent="center" py={1.5}>
                    <Button
                      size="small"
                      onClick={handleLoadMore}
                      disabled={calledsLoadingMore}
                      startIcon={calledsLoadingMore ? <CircularProgress size={14} /> : null}
                    >
                      {intl.formatMessage({ id: "load.more" })}
                    </Button>
                  </Box>
                )}
              </Paper>
            )}
          </Box>
        )}

        {/* Step 3: Review + confirm */}
        {activeStep === 2 && (
          <Box>
            <Grid container spacing={3} alignItems="stretch">
              <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                <Paper variant="outlined" sx={{ p: 2, width: "100%" }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                    <PersonIcon color="action" fontSize="small" />
                    <Typography variant="subtitle2" fontWeight={600}>
                      {intl.formatMessage({ id: "mass.move.origin" })}
                    </Typography>
                  </Box>
                  <Typography variant="body2">{sourceTechnician?.label}</Typography>
                  {sourceTechnician?.email && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {sourceTechnician.email}
                    </Typography>
                  )}
                </Paper>
              </Grid>

              <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                {editingDestTechnician ? (
                  <Autocomplete
                    options={destOptions}
                    loading={technicianLoading}
                    value={destTechnician}
                    onOpen={handleTechnicianOpen}
                    onChange={(_, v) => {
                      if (v) {
                        setDestTechnician(v);
                        setEditingDestTechnician(false);
                      }
                    }}
                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                    getOptionLabel={(opt) => opt.label || ""}
                    open
                    autoFocus
                    onClose={() => setEditingDestTechnician(false)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={intl.formatMessage({ id: "mass.move.destination" })}
                        variant="outlined"
                        autoFocus
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {technicianLoading ? <CircularProgress color="inherit" size={18} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option.id}>
                        <Avatar sx={{ width: 28, height: 28, mr: 1, fontSize: 13, bgcolor: "success.main" }}>
                          {(option.label || "?")[0].toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2">{option.label}</Typography>
                          {option.email && (
                            <Typography variant="caption" color="text.secondary">
                              {option.email}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                    fullWidth
                  />
                ) : (
                  <Paper variant="outlined" sx={{ p: 2, width: "100%" }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                      <SwapHorizIcon color="primary" fontSize="small" />
                      <Typography variant="subtitle2" fontWeight={600} sx={{ flexGrow: 1 }}>
                        {intl.formatMessage({ id: "mass.move.destination" })}
                      </Typography>
                      <IconButton size="small" onClick={() => setEditingDestTechnician(true)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Typography variant="body2">{destTechnician?.label}</Typography>
                    {destTechnician?.email && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {destTechnician.email}
                      </Typography>
                    )}
                  </Paper>
                )}
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label={intl.formatMessage({ id: "mass.move.reason" })}
                  value={reason}
                  onChange={(e) => setReason(e.target.value.slice(0, 255))}
                  inputProps={{ maxLength: 255 }}
                  helperText={`${reason.length}/255`}
                  multiline
                  rows={2}
                  fullWidth
                  required
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {intl.formatMessage({ id: "mass.move.selected.calleds.list" })}
                  </Typography>
                  <Chip
                    size="small"
                    label={intl.formatMessage(
                      { id: "mass.move.calleds.count" },
                      { count: selectedCalledIds.length }
                    )}
                    color="primary"
                  />
                </Box>
                <Box sx={{ maxHeight: 215, overflowY: "auto", overflowX: "hidden", p: 1 }}>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 1.5, p: 0.5 }}>
                  {selectedCalleds.map((called) => (
                    <Paper
                      key={called.id}
                      variant="outlined"
                      component="a"
                      href={getCalledUrl(called.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        p: 1.25, borderRadius: 1.5, display: "flex", flexDirection: "column", gap: 0.5,
                        textDecoration: "none", cursor: "pointer",
                        transition: "transform 0.15s ease, box-shadow 0.15s ease",
                        "&:hover": { transform: "translateY(-2px)", boxShadow: 3 },
                        "&:active": { transform: "translateY(0px)", boxShadow: 1 },
                      }}
                    >
                      <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                        #{called.id}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                        {called.title}
                      </Typography>
                      {called.organization?.name && (
                        <Box display="flex" alignItems="center" gap={0.3}>
                          <BusinessIcon sx={{ fontSize: 11, color: "text.disabled", flexShrink: 0 }} />
                          <Typography variant="caption" color="text.disabled" sx={{ lineHeight: 1.2, display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                            {called.organization.name}
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  ))}
                </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}
        </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {/* Back button pinned to the left */}
        <Box sx={{ flexGrow: 1 }}>
          {activeStep > 0 && (
            <Button onClick={handleBack} disabled={moving}>
              {intl.formatMessage({ id: "back" })}
            </Button>
          )}
        </Box>

        {activeStep < steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={
              (activeStep === 0 && (!sourceTechnician || !destTechnician || !reason.trim())) ||
              (activeStep === 1 && selectedCalledIds.length === 0) ||
              calledsLoading
            }
          >
            {intl.formatMessage({ id: "next" })}
          </Button>
        ) : (
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmMove}
            disabled={moving || !reason.trim()}
            startIcon={moving ? <CircularProgress size={16} /> : <SwapHorizIcon />}
          >
            {intl.formatMessage({ id: "mass.move.confirm.button" })}
          </Button>
        )}
      </DialogActions>

      {/* Confirmation dialog */}
      <Dialog open={confirmOpen} onClose={() => !moving && setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={700}>
            {intl.formatMessage({ id: "mass.move.confirm.title" })}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2.5, overflowX: "hidden" }}>
          <DialogContentText component="div">
            {moving ? (
              <Box>
                <Skeleton variant="text" width="75%" height={24} />
                <Skeleton variant="text" width="45%" height={24} sx={{ mb: 2 }} />
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 1.5, p: 0.5 }}>
                  {[...Array(4)].map((_, i) => <Skeleton key={i} variant="rounded" height={70} />)}
                </Box>
              </Box>
            ) : (
              <>
                <Typography variant="body1" fontWeight={500} sx={{ mb: 2.5 }}>
                  {intl.formatMessage(
                    { id: "mass.move.confirm.description" },
                    {
                      source: <strong>{sourceTechnician?.label}</strong>,
                      dest: <strong>{destTechnician?.label}</strong>,
                      count: selectedCalledIds.length,
                    }
                  )}
                </Typography>
                <Box sx={{ maxHeight: 360, overflowY: "auto", overflowX: "hidden", p: 1 }}>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 1.5, p: 0.5 }}>
                  {selectedCalleds.map((c) => (
                    <Paper
                      key={c.id}
                      variant="outlined"
                      component="a"
                      href={getCalledUrl(c.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        p: 1.25, borderRadius: 1.5, display: "flex", flexDirection: "column", gap: 0.5,
                        textDecoration: "none", cursor: "pointer",
                        transition: "transform 0.15s ease, box-shadow 0.15s ease",
                        "&:hover": { transform: "translateY(-2px)", boxShadow: 3 },
                        "&:active": { transform: "translateY(0px)", boxShadow: 1 },
                      }}
                    >
                      <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                        #{c.id}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                        {c.title}
                      </Typography>
                      {c.organization?.name && (
                        <Box display="flex" alignItems="center" gap={0.3}>
                          <BusinessIcon sx={{ fontSize: 11, color: "text.disabled", flexShrink: 0 }} />
                          <Typography variant="caption" color="text.disabled" sx={{ lineHeight: 1.2, display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                            {c.organization.name}
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  ))}
                </Box>
                </Box>
              </>
            )}
          </DialogContentText>
        </DialogContent>
        {!moving && (
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setConfirmOpen(false)}>
              {intl.formatMessage({ id: "cancel" })}
            </Button>
            <Button variant="contained" color="primary" onClick={handleExecuteMove} autoFocus>
              {intl.formatMessage({ id: "confirm" })}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </Dialog>
  );
}

export default injectIntl(MassMoveCalledModal);
